import React from 'react';
import { useEditor } from '../../context/EditorContext';
import { pickLocalFile } from '../editor/ObjectContextMenu';
import { SCHEME_PRESETS, fillToCss } from '../../lib/colors';
import { gradientVariations, TEXTURES, textureFill, LINE_WEIGHTS, DASH_STYLES, dashArray, strokeHasGradient, strokeHasShimmer, gradientStroke, shimmerStroke, solidStroke, SHIMMER_PRESET_IDS } from '../../lib/fills';
import { GRADIENT_PRESETS, normalizeGradient } from '../../lib/gradients';
import { Switch } from '../ui/switch';
import { Image as ImageIcon, Blend, Grid3X3, Layers2, Minus, Ruler, MoveRight, Sparkles } from 'lucide-react';

const hover = 'hover:bg-[hsl(var(--wp-command-hover))]';
const Label = ({ children }) => <div className="text-[11px] font-semibold text-muted-foreground mt-2 mb-1 first:mt-0">{children}</div>;
const More = ({ label, onClick, testId }) => (
  <button type="button" onClick={onClick} className={`w-full mt-2 flex items-center gap-2 px-2 py-1 rounded-sm text-[13px] border-t border-border pt-2 ${hover}`} data-testid={testId}>{label}</button>
);

/**
 * Extra rows for a *fill* ColorPicker: Picture…, Gradient ▸, Texture ▸, Pattern…
 * @param current  current fill object ({type:'solid', color} etc.)
 * @param apply    (fill) => void
 * @param target   'fill' | 'background' | 'textFill' | 'page' — forwarded to the Fill Effects dialog
 */
export const useFillExtras = ({ current, apply, target = 'fill', testPrefix = 'fill' }) => {
  const { state, openDialog } = useEditor();
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  const base = current?.type === 'solid' && current.color ? current.color : current?.gradient?.stops?.[0]?.color || scheme[2]?.hex || '#4a6d8c';
  const variations = gradientVariations(base);

  const gradientTile = (close) => (g) => {
    const active = JSON.stringify(current?.gradient || null) === JSON.stringify(g.fill.gradient);
    return (
      <button key={g.id} type="button" title={g.name} aria-label={g.name} onClick={() => { apply(g.fill); close(); }} className={`h-11 w-full rounded-sm border transition-colors ${active ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary'}`} style={{ background: fillToCss(g.fill) }} data-testid={`${testPrefix}-gradient-${g.id}`} data-state={active ? 'selected' : 'idle'} />
    );
  };

  return [
    {
      id: 'picture', label: 'Picture', icon: ImageIcon, testId: `${testPrefix}-picture`,
      onClick: () => pickLocalFile((src) => apply({ type: 'image', image: { url: src, fit: 'cover' }, color: '#ffffff' })),
    },
    {
      id: 'gradient', label: 'Gradient', icon: Blend, testId: `${testPrefix}-gradient`,
      panel: (close) => (
        <div>
          <Label>Light Variations</Label>
          <div className="grid grid-cols-3 gap-1">{variations.light.map(gradientTile(close))}</div>
          <Label>Dark Variations</Label>
          <div className="grid grid-cols-3 gap-1">{variations.dark.map(gradientTile(close))}</div>
          <Label>Presets</Label>
          <div className="grid grid-cols-6 gap-1">{GRADIENT_PRESETS.slice(0, 12).map((p) => gradientTile(close)({ id: p.id, name: p.name, fill: { type: 'gradient', gradient: p.gradient } }))}</div>
          <More label="More Gradients... (gradient creator)" onClick={() => { close(); openDialog('fill-effects', { target, tab: 'gradient' }); }} testId={`${testPrefix}-gradient-more`} />
        </div>
      ),
    },
    {
      id: 'texture', label: 'Texture', icon: Layers2, testId: `${testPrefix}-texture`,
      panel: (close) => (
        <div>
          <Label>Textures</Label>
          <div className="grid grid-cols-4 gap-1">
            {TEXTURES.map((t) => {
              const active = current?.texture === t.id;
              return <button key={t.id} type="button" title={t.name} aria-label={t.name} onClick={() => { apply(textureFill(t)); close(); }} className={`h-11 w-full rounded-sm border transition-colors ${active ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary'}`} style={{ background: `url(${t.url}) 0 0 / 80px 80px repeat` }} data-testid={`${testPrefix}-texture-${t.id}`} data-state={active ? 'selected' : 'idle'} />;
            })}
          </div>
          <More label="More Textures..." onClick={() => { close(); openDialog('fill-effects', { target, tab: 'picture' }); }} testId={`${testPrefix}-texture-more`} />
        </div>
      ),
    },
    {
      id: 'pattern', label: 'Pattern', icon: Grid3X3, testId: `${testPrefix}-pattern`,
      onClick: () => openDialog('fill-effects', { target, tab: 'pattern' }),
    },
  ];
};

/**
 * Extra rows for an *outline* ColorPicker: Weight ▸, Dashes ▸, Arrows ▸ (lines only), Gradient ▸, More Lines…
 * @param stroke  current stroke object ({color, width, dash, gradient?})
 * @param apply   (strokePatch) => void  — receives a full stroke object
 * @param gradient  false to leave the Gradient ▸ row out (glyph outlines have no gradient)
 */
export const useOutlineExtras = ({ stroke = {}, apply, onMore, showArrows = false, arrows = {}, applyArrows, gradient = true, testPrefix = 'outline' }) => {
  const { state, openDialog } = useEditor();
  const color = stroke.color || '#000000';
  const scheme = SCHEME_PRESETS[state.schemeId] || SCHEME_PRESETS.office;
  const gradientBase = strokeHasGradient(stroke) ? stroke.gradient.stops?.[0]?.color || color : stroke.width > 0 ? color : scheme[2]?.hex || '#4a6d8c';
  const variations = gradientVariations(gradientBase);
  const gradientTile = (close) => (g) => {
    const active = strokeHasGradient(stroke) && JSON.stringify(stroke.gradient) === JSON.stringify(g.fill.gradient);
    return (
      <button key={g.id} type="button" title={g.name} aria-label={g.name} onClick={() => { apply(gradientStroke(stroke, g.fill.gradient)); close(); }} className={`h-11 w-full rounded-sm border transition-colors ${active ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary'}`} style={{ background: fillToCss(g.fill) }} data-testid={`${testPrefix}-gradient-${g.id}`} data-state={active ? 'selected' : 'idle'} />
    );
  };
  const items = [
    {
      id: 'weight', label: 'Weight', icon: Ruler, testId: `${testPrefix}-weight`,
      panel: (close) => (
        <div className="space-y-px" data-testid={`${testPrefix}-weight-panel`}>
          {LINE_WEIGHTS.map((w) => {
            const active = Number(stroke.width) === w;
            return (
              <button key={w} type="button" onClick={() => { apply({ ...stroke, color, width: w }); close(); }} className={`w-full flex items-center gap-3 px-2 py-1 rounded-sm text-[12px] ${hover} ${active ? 'bg-primary/10' : ''}`} data-testid={`${testPrefix}-weight-${String(w).replace('.', '_')}`} data-state={active ? 'selected' : 'idle'}>
                <span className="flex-1 flex items-center"><span className="block w-full rounded-full" style={{ height: Math.max(1, w), background: 'currentColor' }} /></span>
                <span className="w-12 text-right text-muted-foreground">{w} pt</span>
              </button>
            );
          })}
          {onMore && <More label="More Lines..." onClick={() => { close(); onMore(); }} testId={`${testPrefix}-weight-more`} />}
        </div>
      ),
    },
    {
      id: 'dashes', label: 'Dashes', icon: Minus, testId: `${testPrefix}-dashes`,
      panel: (close) => (
        <div className="space-y-px" data-testid={`${testPrefix}-dashes-panel`}>
          {DASH_STYLES.map((d) => {
            const active = (stroke.dash || 'solid') === d.id;
            return (
              <button key={d.id} type="button" onClick={() => { apply({ ...stroke, color, width: stroke.width || 1, dash: d.id }); close(); }} className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-sm text-[12px] ${hover} ${active ? 'bg-primary/10' : ''}`} title={d.name} data-testid={`${testPrefix}-dash-${d.id}`} data-state={active ? 'selected' : 'idle'}>
                <svg width="120" height="6" className="flex-1" aria-hidden="true"><line x1="0" y1="3" x2="120" y2="3" stroke="currentColor" strokeWidth="2" strokeDasharray={dashArray(d.id)} strokeLinecap={d.id === 'dotted' ? 'round' : 'butt'} /></svg>
                <span className="w-24 text-right text-muted-foreground truncate">{d.name}</span>
              </button>
            );
          })}
          {onMore && <More label="More Lines..." onClick={() => { close(); onMore(); }} testId={`${testPrefix}-dashes-more`} />}
        </div>
      ),
    },
  ];
  if (showArrows && applyArrows) {
    items.push({
      id: 'arrows', label: 'Arrows', icon: MoveRight, testId: `${testPrefix}-arrows`,
      panel: (close) => (
        <div className="space-y-px" data-testid={`${testPrefix}-arrows-panel`}>
          {[
            ['none', 'No Arrows', false, false], ['end', 'Arrow at End', false, true], ['start', 'Arrow at Start', true, false], ['both', 'Arrows at Both Ends', true, true],
          ].map(([id, name, s, e]) => {
            const active = !!arrows.arrowStart === s && !!arrows.arrowEnd === e;
            return (
              <button key={id} type="button" onClick={() => { applyArrows({ arrowStart: s, arrowEnd: e }); close(); }} className={`w-full flex items-center gap-3 px-2 py-1.5 rounded-sm text-[12px] ${hover} ${active ? 'bg-primary/10' : ''}`} data-testid={`${testPrefix}-arrows-${id}`} data-state={active ? 'selected' : 'idle'}>
                <svg width="120" height="10" className="flex-1" aria-hidden="true">
                  <line x1="10" y1="5" x2="110" y2="5" stroke="currentColor" strokeWidth="2" />
                  {s && <path d="M10,5 L18,1 L18,9 Z" fill="currentColor" />}
                  {e && <path d="M110,5 L102,1 L102,9 Z" fill="currentColor" />}
                </svg>
                <span className="w-28 text-right text-muted-foreground truncate">{name}</span>
              </button>
            );
          })}
        </div>
      ),
    });
  }
  if (gradient) {
    items.push({
      id: 'gradient', label: 'Gradient', icon: Blend, testId: `${testPrefix}-gradient`,
      panel: (close) => (
        <div data-testid={`${testPrefix}-gradient-panel`}>
          <Label>Light Variations</Label>
          <div className="grid grid-cols-3 gap-1">{variations.light.map(gradientTile(close))}</div>
          <Label>Dark Variations</Label>
          <div className="grid grid-cols-3 gap-1">{variations.dark.map(gradientTile(close))}</div>
          <Label>Presets</Label>
          <div className="grid grid-cols-6 gap-1">{GRADIENT_PRESETS.slice(0, 12).map((p) => gradientTile(close)({ id: p.id, name: p.name, fill: { type: 'gradient', gradient: p.gradient } }))}</div>
          <Label>Shimmer — metallic outline with a moving highlight (screen only)</Label>
          <div className="grid grid-cols-3 gap-1" data-testid={`${testPrefix}-shimmer-tiles`}>
            {SHIMMER_PRESET_IDS.map((pid) => {
              const preset = GRADIENT_PRESETS.find((g) => g.id === pid);
              if (!preset) return null;
              const active = strokeHasShimmer(stroke) && JSON.stringify(stroke.gradient) === JSON.stringify(normalizeGradient(preset.gradient));
              return (
                <button key={pid} type="button" title={`${preset.name} shimmer`} aria-label={`${preset.name} shimmer`} onClick={() => { apply(shimmerStroke(stroke, preset.gradient)); close(); }} className={`relative h-11 w-full rounded-sm border transition-colors ${active ? 'border-primary ring-2 ring-primary/30' : 'border-border hover:border-primary'}`} style={{ background: fillToCss({ type: 'gradient', gradient: preset.gradient }) }} data-testid={`${testPrefix}-shimmer-${pid}`} data-state={active ? 'selected' : 'idle'}>
                  <span className="absolute inset-0 rounded-sm wp-shimmer-tile" aria-hidden="true" />
                  <Sparkles className="absolute right-1 top-1 h-3.5 w-3.5 text-white drop-shadow" aria-hidden="true" />
                  <span className="absolute left-1.5 bottom-1 text-[10px] font-semibold text-white drop-shadow">{preset.name}</span>
                </button>
              );
            })}
          </div>
          {strokeHasGradient(stroke) && (
            <label className={`mt-2 flex items-center justify-between gap-2 px-2 py-1 rounded-sm text-[13px] cursor-pointer ${hover}`} data-testid={`${testPrefix}-shimmer-toggle-row`}>
              <span className="inline-flex items-center gap-2"><Sparkles className="h-3.5 w-3.5 text-muted-foreground" aria-hidden="true" />Animated shimmer on this outline</span>
              <Switch checked={!!stroke.shimmer} onCheckedChange={(on) => apply({ ...stroke, shimmer: !!on })} aria-label="Animated shimmer" data-testid={`${testPrefix}-shimmer-toggle`} />
            </label>
          )}
          {strokeHasGradient(stroke) && <More label="Solid colour outline (remove the gradient)" onClick={() => { apply(solidStroke(stroke, color)); close(); }} testId={`${testPrefix}-gradient-solid`} />}
          <More label="More Gradients... (gradient creator)" onClick={() => { close(); openDialog('fill-effects', { target: 'stroke', tab: 'gradient' }); }} testId={`${testPrefix}-gradient-more`} />
        </div>
      ),
    });
  }
  return items;
};
